import { MUSIC_DEFAULT_LEVEL, MUSIC_TRACK_GAP_MS, type MusicTransport, type MusicTrack, type RoomMusicState } from './RoomMusic.js';

export function emptyRoomMusic(roomId: string, nowMs: number): RoomMusicState {
  return { roomId, revision: 0, sourceKind: 'catalog', playlist: null, queue: [], previous: null,
    status: 'off', pauseReason: null, currentIndex: 0, virtualStartAtMs: null, pausedPositionMs: 0,
    effectiveAtMs: nowMs, next: null, level: MUSIC_DEFAULT_LEVEL, includeInProgram: true,
    programAlignmentMs: 0, yieldedBy: [], lastCommandBy: null, serverTimeMs: nowMs };
}
export function musicTransport(state: MusicTransport): MusicTransport {
  return { status: state.status, pauseReason: state.pauseReason, currentIndex: state.currentIndex,
    virtualStartAtMs: state.virtualStartAtMs, pausedPositionMs: state.pausedPositionMs,
    effectiveAtMs: state.effectiveAtMs, next: state.next };
}
export function scheduledMusicNext(queue: MusicTrack[], index: number, startAtMs: number | null) {
  if (startAtMs === null || !queue.length) return null;
  return { index: (index + 1) % queue.length, startAtMs: startAtMs + queue[index].durationMs + MUSIC_TRACK_GAP_MS };
}
/** Pure timeline projection shared by the server, browsers and feeder.
 * Whole-cycle skipping makes a read after a long outage bounded by queue length.
 */
export function projectMusicTransport(transport: MusicTransport, queue: MusicTrack[], serverNowMs: number): MusicTransport {
  if (transport.status !== 'playing' || transport.virtualStartAtMs === null || !queue.length) return musicTransport(transport);
  let start = transport.virtualStartAtMs;
  let index = transport.currentIndex;
  const cycle = queue.reduce((sum, track) => sum + track.durationMs + MUSIC_TRACK_GAP_MS, 0);
  if (serverNowMs >= start + cycle) start += Math.floor((serverNowMs - start) / cycle) * cycle;
  for (let count = 0; count < queue.length; count++) {
    const next = scheduledMusicNext(queue, index, start)!;
    if (serverNowMs < next.startAtMs) break;
    start = next.startAtMs; index = next.index;
  }
  return { ...musicTransport(transport), currentIndex: index, virtualStartAtMs: start, next: scheduledMusicNext(queue, index, start) };
}
export function musicAt(state: RoomMusicState, serverNowMs: number) {
  const pending = serverNowMs < state.effectiveAtMs && state.previous;
  const queue = pending ? pending.queue : state.queue;
  const playlist = pending ? pending.playlist ?? null : state.playlist;
  const transport = projectMusicTransport(pending || state, queue, serverNowMs);
  const track = queue[transport.currentIndex] ?? null;
  const elapsed = transport.virtualStartAtMs === null ? transport.pausedPositionMs : serverNowMs - transport.virtualStartAtMs;
  const positionMs = Math.max(0, Math.min(track?.durationMs ?? 0, elapsed));
  const audible = transport.status === 'playing' && !!track && elapsed >= 0 && elapsed < track.durationMs;
  return { transport, queue, playlist, track, positionMs, audible };
}
